

import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope, faUser, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faYoutube,
  faTwitter,
  faInstagram,
  faThreads
} from "@fortawesome/free-brands-svg-icons";
import logo from "../assets/logo.png";
import { Link } from "react-router-dom";
import { getDatabase, ref, set, onValue } from "firebase/database";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";

const Navbar = () => {
  const [destinations, setDestinations] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [user, setUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const db = getDatabase();
  const auth = getAuth();
  const navigate = useNavigate();



  // Fetch destinations from Firebase
  useEffect(() => {
    const destinationsRef = ref(db, "packages");
    const unsubscribe = onValue(destinationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const destinationNames = Object.keys(data);
        setDestinations(destinationNames);
      } else {
        setDestinations([]);
      }
    });
    return () => unsubscribe();
  }, [db]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setSuccess("Logged out successfully!");
    } catch (err) {
      setError("Error signing out. Please try again.");
    }
  };

  const resetForm = () => {
    setName("");
    setAddress("");
    setMobileNumber("");
    setEmail("");
    setPassword("");
    setError("");
    setSuccess("");
  };

  const closeAllPopups = () => {
    setShowLogin(false);
    setShowSignup(false);
    setShowForgotPassword(false);
    resetForm();
  };

  const getAuthErrorMessage = (code) => {
    switch (code) {
      case "auth/invalid-email":
        return "Invalid email address.";
      case "auth/user-disabled":
        return "This account has been disabled.";
      case "auth/user-not-found":
        return "No account found with this email.";
      case "auth/wrong-password":
        return "Incorrect password.";
      case "auth/email-already-in-use":
        return "This email is already in use.";
      case "auth/weak-password":
        return "Password should be at least 6 characters.";
      case "auth/operation-not-allowed":
        return "Email/password accounts are not enabled.";
      case "auth/too-many-requests":
        return "Too many requests. Try again later.";
      default:
        return "An error occurred. Please try again.";
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (dropdownName) => {
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdownName);
    }
  };

  const midIndex = Math.ceil(destinations.length / 2);
  const column1 = destinations.slice(0, midIndex);
  const column2 = destinations.slice(midIndex);

  return (
    <>
      <div className="navbar-container">
        <header>
          {/* Top Bar with Logo, Contact Info, and Social Icons */}
          <div className="top-bar">
            <div className="logo-contact">
              <Link to="/" aria-label="Home">
                <img
                  src={logo}
                  alt="Pearl International"
                  className="logo"
                />
              </Link>

              <div className="contact-info-container">
                <span className="contact-info">
                  <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                  +91 73034 55505
                </span>
                <span className="contact-info">
                  <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                  pearltoursandtravelsangli@gmail.com
                </span>
              </div>
            </div>

            <div className="social-icons">
              <Link
                to="https://www.facebook.com/pearltoursandtravelsangli/about/?_rdr/"
                target="_blank"
                aria-label="Facebook"
                className="social-link"
              >
                <FontAwesomeIcon icon={faFacebookF} className="social-icon" />
              </Link>
              <Link
                to="https://www.youtube.com/@pearltourstravelssangli4768/"
                target="_blank"
                aria-label="YouTube"
                className="social-link"
              >
                <FontAwesomeIcon icon={faYoutube} className="social-icon" />
              </Link>
              <Link
                to="/"
                aria-label="Twitter"
                className="social-link"
              >
                <FontAwesomeIcon icon={faTwitter} className="social-icon" />
              </Link>
              <Link
                to="https://www.instagram.com/pearl_tours_travels_sangli/"
                target="_blank"
                aria-label="Instagram"
                className="social-link"
              >
                <FontAwesomeIcon icon={faInstagram} className="social-icon" />
              </Link>
              <Link
                to="https://www.threads.com/@pearl_tours_travels_sangli"
                target="_blank"
                aria-label="Threads"
                className="social-link"
              >
                <FontAwesomeIcon icon={faThreads} className="social-icon" />
              </Link>
            </div>
          </div>

          {/* Main Navigation Bar - Always Visible */}
          <div className="main-nav-container">
            <nav className="main-nav">
              {/* Mobile Menu Button - Only visible on mobile */}
              <button className="mobile-menu-button" onClick={toggleMobileMenu}>
                <FontAwesomeIcon 
                  icon={isMobileMenuOpen ? faTimes : faBars} 
                  className="menu-icon" 
                />
                <span className="menu-text">
                  {isMobileMenuOpen ? "Close" : "Menu"}
                </span>
              </button>

              {/* Navigation Links - Always Visible */}
              <ul className={`nav-links ${isMobileMenuOpen ? "mobile-open" : ""}`}>
                <li>
                  <Link
                    to="/"
                    className="nav-link"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setActiveDropdown(null);
                    }}
                  >
                    Home
                  </Link>
                </li>

                <li 
                  className={`dropdown-container ${activeDropdown === 'tours' ? 'active' : ''}`}
                  onClick={() => window.innerWidth < 1024 && toggleDropdown('tours')}
                  onMouseEnter={() => window.innerWidth >= 1024 && setActiveDropdown('tours')}
                  onMouseLeave={() => window.innerWidth >= 1024 && setActiveDropdown(null)}
                >
                  <span className="nav-link">
                    Tours
                  </span>

                  <ul className={`dropdown dropdown-menu ${activeDropdown === 'tours' ? 'active' : ''}`}>
                    <li>
                      <Link
                        to="/international-tours"
                        className="dropdown-link"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setActiveDropdown(null);
                        }}
                      >
                        International Tours
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/domestic-tours"
                        className="dropdown-link"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setActiveDropdown(null);
                        }}
                      >
                        Domestic Tours
                      </Link>
                    </li>
                  </ul>
                </li>

                <li className="dropdown-container">
                  <div
                    className="dropdown-wrapper"
                    onMouseEnter={() => window.innerWidth >= 1024 && setActiveDropdown("places")}
                    onMouseLeave={() => window.innerWidth >= 1024 && setActiveDropdown(null)}
                  >
                    <span
                      className="nav-link"
                      onClick={() => window.innerWidth < 1024 && toggleDropdown("places")}
                    >
                      Places
                    </span>

                    {activeDropdown === "places" && (
                      <ul className="places-dropdown dropdown-menu">
                        <div className="dropdown-column">
                          {column1.map((destination) => (
                            <li key={destination}>
                              <Link
                                to={`/destination/${destination.toLowerCase().replace(/\s+/g, "-")}`}
                                className="dropdown-link"
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setActiveDropdown(null);
                                }}
                              >
                                {destination}
                              </Link>
                            </li>
                          ))}
                        </div>

                        <div className="dropdown-column">
                          {column2.map((destination) => (
                            <li key={destination}>
                              <Link
                                to={`/destination/${destination.toLowerCase().replace(/\s+/g, "-")}`}
                                className="dropdown-link"
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setActiveDropdown(null);
                                }}
                              >
                                {destination}
                              </Link>
                            </li>
                          ))}
                        </div>
                      </ul>
                    )}
                  </div>
                </li>

                <li
                  onClick={() => {
                    navigate("/Visa");
                    setIsMobileMenuOpen(false);
                    setActiveDropdown(null);
                  }}
                >
                  <span className="nav-link">
                    Visa
                  </span>
                </li>

                <li>
                  <Link
                    to="/Blog"
                    className="nav-link"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setActiveDropdown(null);
                    }}
                  >
                    Blog
                  </Link>
                </li>
              </ul>

              {/* Action Buttons - Always Visible */}
              <div className="action-buttons">
                {user ? (
                  <>
                    <span className="user-info">
                      <FontAwesomeIcon icon={faUser} className="user-icon" />
                      {user.email.split("@")[0]}
                    </span>
                    <button
                      className="logout-btn"
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                        setActiveDropdown(null);
                      }}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/AdminLogin">
                    <button
                      className="admin-login-btn ad-btn"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setActiveDropdown(null);
                      }}
                    >
                      Admin Login
                    </button>
                  </Link>
                )}

                <Link
                  to="/agent-connect"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button
                    className="agent-btn"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setActiveDropdown(null);
                    }}
                  >
                    Agent Connect
                  </button>
                </Link>
              </div>
            </nav>
          </div>
        </header>
      </div>

      <style jsx>{`
        .dropdown-wrapper {
          position: relative;
          display: inline-block;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          display: block;
          background-color: white;
          z-index: 1000;
        }

        .dropdown-menu.active {
          display: block;
        }

        .navbar-container {
          background-color: #f8f8f8;
          border-bottom: 1px solid #e5e5e5;
          position: sticky;
          top: 0;
          width: 100%;
          z-index: 1000;
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
          font-family: "Segoe UI", sans-serif;
        }

        .top-bar {
          background-color: #f8f8f8;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 20px;
          font-size: 17px;
          border-bottom: 1px solid #e5e5e5;
        }

        .logo-contact {
          display: flex;
          align-items: center;
          gap: 20px;
          color: #444;
          width: 100%;
        }

        .contact-info-container {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .logo {
          height: 60px;
          width: auto;
        }

        .contact-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          white-space: nowrap;
        }

        .contact-icon {
          color: #64192E;
        }

        .social-icons {
          display: none; /* Hidden on mobile by default */
          align-items: center;
          gap: 14px;
        }

        .social-link {
          color: #555;
          text-decoration: none;
        }

        .social-icon {
          font-size: 16px;
          transition: color 0.3s;
        }

        .social-icon:hover {
          color: #64192E;
        }

        .main-nav-container {
          background-color: #f8f8f8;
          border-bottom: 1px solid #e5e5e5;
        }

        .main-nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 20px;
          position: relative;
        }

        .mobile-menu-button {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          z-index: 1001;
          color: #64192E;
          font-weight: 500;
        }

        .menu-icon {
          font-size: 24px;
          margin-right: 8px;
        }

        .menu-text {
          font-size: 16px;
        }

        .nav-links {
          display: flex;
          list-style: none;
          margin: 0;
          padding: 0;
          font-size: 16px;
          font-weight: 500;
        }

        .nav-link {
          color: #333;
          text-decoration: none;
          padding: 10px 15px;
          display: block;
          transition: color 0.3s;
          cursor: pointer;
        }

        .nav-link:hover {
          color: #64192E;
        }

        .dropdown-container {
          position: relative;
        }

        .dropdown-menu {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          background-color: #fff;
          padding: 10px 0;
          list-style: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          min-width: 160px;
          z-index: 9999;
          border-radius: 4px;
        }

        .dropdown-menu.active {
          display: block;
        }

        .places-dropdown {
          min-width: 400px;
          padding: 15px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .dropdown-link {
          display: block;
          padding: 10px 20px;
          text-decoration: none;
          color: #333;
          white-space: nowrap;
          transition: background 0.3s, color 0.3s;
        }

        .dropdown-link:hover {
          background: #f1f1f1;
          color: #64192E;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .user-info {
          display: flex;
          align-items: center;
          color: #64192E;
          font-weight: 500;
          margin-right: 10px;
        }

        .user-icon {
          margin-right: 6px;
        }

        .admin-login-btn {
          background: none;
          color: #64192E;
          border: 1px solid #64192E;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          transition: background 0.3s ease;
          font-size: 13px;
        }

        .admin-login-btn:hover {
          background: #64192E;
          color: #fff;
        }

        .logout-btn {
          background: #64192E;
          color: #fff;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          transition: background 0.3s ease;
          font-size: 16px;
        }

        .logout-btn:hover {
          background: #c0392b;
        }

        .agent-btn {
          border: 1px solid #64192E;
          background: none;
          color: #64192E;
          border-radius: 4px;
          padding: 8px 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 13px;
        }

        .agent-btn:hover {
          background: #64192E;
          color: #fff;
        }

        /* Mobile Styles */
        @media (max-width: 1023px) {
          .top-bar {
            flex-direction: column;
            align-items: flex-start;
            padding: 10px;
          }

          .logo-contact {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
            display: flex;
            flex-direction: row;
          }
          .ad-btn{
            display:none;
          }
          .contact-info-container {
            width: 100%;
          }

          .logo {
            height: 50px;
          }

          .contact-info {
            font-size: 13px;
          }

          .social-icons {
            display: none;
          }

          .main-nav {
            flex-direction: column;
            align-items: flex-start;
            padding: 10px;
          }

          .nav-links {
            flex-direction: column;
            width: 100%;
            display: ${isMobileMenuOpen ? 'flex' : 'none'};
            margin-top: 10px;
          }

          .nav-link {
            padding: 12px 0;
            border-bottom: 1px solid #e5e5e5;
          }

          .dropdown-container {
            width: 100%;
          }

          .dropdown-menu {
            position: static;
            width: 100%;
            box-shadow: none;
            padding-left: 15px;
          }

          .places-dropdown {
            min-width: 100%;
            grid-template-columns: 1fr;
            padding-left: 15px;
          }

          .action-buttons {
            width: 100%;
            margin-top: 10px;
            flex-direction: column;
            gap: 10px;
            display: ${isMobileMenuOpen ? 'flex' : 'none'};
          }

          .admin-login-btn, .logout-btn, .agent-btn {
            width: 100%;
            text-align: center;
          }
        }

        /* Tablet Styles */
        @media (min-width: 768px) and (max-width: 1023px) {
          .top-bar {
            flex-direction: row;
            align-items: center;
          }

          .logo-contact {
            flex-direction: row;
            align-items: center;
            
          }

          .contact-info-container {
            flex-direction: row;
            gap: 20px;
          }

          .social-icons {
            display: flex;
          }
        }

        /* Desktop Styles */
        @media (min-width: 1024px) {
          .top-bar {
            padding: 10px 60px;
          }

          .logo-contact {
            flex-direction: row;
            align-items: center;
          }

          .contact-info-container {
            flex-direction: row;
            gap: 20px;
          }

          .logo {
            height: 100px;
            margin-right: 100px;
          }

          .contact-info {
            font-size: 17px;
          }

          .social-icons {
            display: flex;
          }

          .mobile-menu-button {
            display: none;
          }

          .main-nav {
            padding: 0 60px;
            flex-direction: row;
          }

          .nav-links {
            flex-direction: row;
            width: auto;
            display: flex !important;
          }

          .action-buttons {
            width: auto;
            margin-top: 0;
            flex-direction: row;
            display: flex !important;
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;